/**
 * Multi-Tab Auth Sync Hook
 *
 * Synchronizes authentication state across browser tabs using the
 * Storage event API. When a user logs in or out in one tab, all
 * other tabs are automatically updated.
 *
 * Features:
 * - Sync logout across all tabs
 * - Sync login across all tabs
 * - Sync token refresh across all tabs
 * - Handle session expiry uniformly
 *
 * Usage:
 *   // In AuthProvider or App.tsx
 *   useAuthSync({
 *     onLogout: () => {
 *       // Clear local auth state
 *       setUser(null);
 *       navigate('/login');
 *     },
 *     onLogin: (user) => {
 *       // Update local auth state
 *       setUser(user);
 *     },
 *   });
 */

import { useEffect, useCallback, useRef } from "react";
import { STORAGE_KEYS } from "@/constants";

// Sync event types
type AuthSyncEventType = "logout" | "login" | "refresh" | "session_expired";

interface AuthSyncEvent {
  type: AuthSyncEventType;
  timestamp: number;
  data?: {
    userId?: string;
    tenantId?: string;
  };
}

interface UseAuthSyncOptions {
  /** Callback when logout is detected in another tab */
  onLogout?: () => void;
  /** Callback when login is detected in another tab */
  onLogin?: (data?: { userId?: string; tenantId?: string }) => void;
  /** Callback when token refresh is detected in another tab */
  onRefresh?: () => void;
  /** Callback when session expires */
  onSessionExpired?: () => void;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Fired on `window` when THIS tab's session has ended (the API client's
 * refresh was refused or gave up). The storage events below only reach
 * OTHER tabs, and the API client cannot import the auth store (the store
 * imports the client), so the store learns about it through this event.
 */
export const SESSION_ENDED_EVENT = "ilmiq:session-ended";

/**
 * The session in this tab is over: tell this tab's auth store, and tell the
 * other tabs.
 */
export function notifySessionEnded(): void {
  try {
    window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
  } catch {
    // No window (tests without a DOM): nothing to tell.
  }
  broadcastAuthEvent("session_expired");
}

/**
 * Run `handler` when this tab's session ends (see notifySessionEnded).
 */
export function useSessionEnded(handler: () => void): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  useEffect(() => {
    const onEnded = () => handlerRef.current();
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, []);
}

/**
 * Broadcast an auth event to other tabs
 */
export function broadcastAuthEvent(
  type: AuthSyncEventType,
  data?: { userId?: string; tenantId?: string }
): void {
  try {
    const event: AuthSyncEvent = {
      type,
      timestamp: Date.now(),
      data,
    };

    // Use different keys for different event types for better reliability
    const key = type === "logout" ? STORAGE_KEYS.AUTH_LOGOUT :
                type === "login" ? STORAGE_KEYS.AUTH_LOGIN :
                type === "refresh" ? STORAGE_KEYS.AUTH_REFRESH :
                STORAGE_KEYS.AUTH_SYNC;

    // Write to localStorage to trigger storage event in other tabs
    localStorage.setItem(key, JSON.stringify(event));

    // Clean up immediately - we only need to trigger the event
    // Use setTimeout to ensure the event fires before removal.
    //
    // The catch below cannot reach this callback: it runs 100ms later, on
    // its own tick, with an empty stack. If the window is gone by then the
    // throw escapes as an UNCAUGHT exception. In CI that failed `UI / Tests`
    // with every test passing -- `Tests 515 passed` alongside
    // `Errors 3 errors` -- because a test file finished inside the 100ms and
    // jsdom had already torn `localStorage` down. Hence the inner guard.
    setTimeout(() => {
      try {
        localStorage.removeItem(key);
      } catch {
        // Window torn down (test teardown, or a tab closing mid-broadcast).
        // The storage entry goes with it, so there is nothing left to clean.
      }
    }, 100);
  } catch (error) {
    console.warn("Failed to broadcast auth event:", error);
  }
}

/**
 * Hook to sync authentication state across browser tabs
 */
export function useAuthSync({
  onLogout,
  onLogin,
  onRefresh,
  onSessionExpired,
  enabled = true,
}: UseAuthSyncOptions = {}): {
  broadcastLogout: () => void;
  broadcastLogin: (data?: { userId?: string; tenantId?: string }) => void;
  broadcastRefresh: () => void;
  broadcastSessionExpired: () => void;
} {
  // Use refs to avoid stale closure issues
  const onLogoutRef = useRef(onLogout);
  const onLoginRef = useRef(onLogin);
  const onRefreshRef = useRef(onRefresh);
  const onSessionExpiredRef = useRef(onSessionExpired);

  // Update refs when callbacks change
  useEffect(() => {
    onLogoutRef.current = onLogout;
    onLoginRef.current = onLogin;
    onRefreshRef.current = onRefresh;
    onSessionExpiredRef.current = onSessionExpired;
  }, [onLogout, onLogin, onRefresh, onSessionExpired]);

  // Listen for storage events from other tabs
  useEffect(() => {
    if (!enabled) return;

    const handleStorageEvent = (event: StorageEvent) => {
      // Only handle our auth sync keys
      if (
        event.key !== STORAGE_KEYS.AUTH_LOGOUT &&
        event.key !== STORAGE_KEYS.AUTH_LOGIN &&
        event.key !== STORAGE_KEYS.AUTH_REFRESH &&
        event.key !== STORAGE_KEYS.AUTH_SYNC
      ) {
        return;
      }

      // Ignore if no new value (happens on removal)
      if (!event.newValue) return;

      try {
        const syncEvent: AuthSyncEvent = JSON.parse(event.newValue);

        // Handle based on event type
        switch (syncEvent.type) {
          case "logout":
            onLogoutRef.current?.();
            break;
          case "login":
            onLoginRef.current?.(syncEvent.data);
            break;
          case "refresh":
            onRefreshRef.current?.();
            break;
          case "session_expired":
            onSessionExpiredRef.current?.();
            break;
        }
      } catch (error) {
        console.warn("Failed to parse auth sync event:", error);
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [enabled]);

  // Broadcast functions
  const broadcastLogout = useCallback(() => {
    broadcastAuthEvent("logout");
  }, []);

  const broadcastLogin = useCallback(
    (data?: { userId?: string; tenantId?: string }) => {
      broadcastAuthEvent("login", data);
    },
    []
  );

  const broadcastRefresh = useCallback(() => {
    broadcastAuthEvent("refresh");
  }, []);

  const broadcastSessionExpired = useCallback(() => {
    broadcastAuthEvent("session_expired");
  }, []);

  return {
    broadcastLogout,
    broadcastLogin,
    broadcastRefresh,
    broadcastSessionExpired,
  };
}

/**
 * Hook specifically for handling logout sync
 * Simpler API for common use case
 */
export function useLogoutSync(onLogout: () => void): {
  broadcastLogout: () => void;
} {
  const { broadcastLogout } = useAuthSync({ onLogout });
  return { broadcastLogout };
}

export default useAuthSync;
